<template>
  <k-panel :hasContentToggle="false" :title="pageTitle">
    <template #body>
      <meeting-meta v-if="meetingMeta" v-model="meetingMeta" @is-valid="e => meetingMetaValid = e"></meeting-meta>
      <k-panel v-if="displayFormSection" :hasContentToggle="false">
        <template #body>
          <template v-if="renderMode">
            <p class="read-only">Read-Only</p>
            <k-questionnaire :questionnaire="meetingForm" :read-only="true"></k-questionnaire>
          </template>
          <meeting-form-builder v-else
            v-model="meetingForm"
            @is-valid="e => meetingFormValid = e"
          ></meeting-form-builder>
        </template>
      </k-panel>
      <div class="controls">
        <template v-if="displayFormSection">
          <button v-if="renderMode" class="btn btn-primary" @click="enterEditMode">
            Edit Meeting Form
            <i class="fas fa-edit" aria-hidden="true"></i>
          </button>
          <button v-else class="btn btn-primary" @click="enterRenderMode">
            <i class="fas fa-eye" aria-hidden="true"></i>
            Preview Meeting Form
          </button>
        </template>
        <button class="btn btn-success update-button" @click="updateMeeting"
          :disabled="!validMeeting || updateInProgress">
          <i class="fa fa-spinner fa-spin" v-if="updateInProgress" aria-hidden="true"></i>
          Update Meeting
        </button>
      </div>
    </template>
  </k-panel>
</template>

<style scoped>
.controls > button:last-child {
  float: right;
  margin-left: auto;
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import MeetingFormBuilder from './meeting-form-builder.vue';
import KQuestionnaire from '../../../../questionnaires/k-questionnaire.vue';
import MeetingMeta from './meeting-meta.vue';

export default {
  components: {
    KPanel,
    MeetingMeta,
    KQuestionnaire,
    MeetingFormBuilder,
  },

  data() {
    return {
      meetingReady: false,
      meeting: undefined,
      meetingForm: undefined,
      meetingMeta: undefined,
      renderMode: false,
      updateInProgress: false,
      meetingMetaValid: false,
      meetingFormValid: false,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getMeeting();
  },

  watch: {
    meetingReady() {
      if (this.meetingReady) {
        this.$Loading.finish();
      } else {
        this.$Loading.start();
      }
    },
    meeting() {
      if (this.meeting) {
        this.meetingMeta = {
          title: this.meeting.name || '',
          description: this.meeting.description || '',
          meetingTags: this.meeting.tags || [],
          supportFunction: this.meeting.support_function || 'coach',
          hasForm: this.meeting.is_progress_review || false,
          isAdhoc: this.meeting.is_adhoc || false,
          requiresLearnerSignature: this.meeting.requires_learner_signature ?? true,
          shareWithEmployerDefault: this.meeting.share_with_employer_default ?? true,
        };
        this.meetingForm = {
          id: this.meeting.meeting_form_id,
          prompts: this.meeting.meeting_form_prompts?.map(p => ({
            id: p.id || null,
            prompt: p.prompt || '',
            responseType: p.response_type || 'TEXT',
            reviewType: p.review_type || 'REQUIRED',
            answers: p.answers,
            number: p.number,
          })) || [],
          reviewType: this.meeting.meeting_form_review_type || '',
          description: this.meeting.meeting_form_description || '',
        };
      } else {
        this.meetingMeta = undefined;
      }
    },
  },

  computed: {
    meetingId() {
      return parseInt(this.$route.params.meetingId, 10);
    },
    pageTitle() {
      return `${this.meetingId ? 'Edit' : 'Create'} Meeting`;
    },
    sectionTitle() {
      if (this.renderMode) {
        return `Preview of '${this.meetingMeta.title}' Form`;
      }
      return this.meetingForm.id ? 'Update Meeting Form' : 'Create Meeting Form';
    },
    displayFormSection() {
      return this.meetingMeta && this.meetingForm && this.meetingMeta.hasForm;
    },
    validMeeting() {
      if (this.meetingMeta?.hasForm) {
        return this.meetingMetaValid && this.meetingFormValid;
      }
      return this.meetingMetaValid;
    },
    meetingFormPayload() {
      return {
        meeting_id: this.meeting.id,
        review_type: this.meetingForm.reviewType,
        description: this.meetingForm.description,
        prompts: this.meetingForm.prompts.map((p, i) => ({
          id: p.id || null,
          prompt: p.prompt,
          response_type: p.responseType,
          review_type: p.reviewType,
          answers: p.responseType === 'MULTIPLE_CHOICE' ? p.answers : null,
          number: i + 1,
        })),
      };
    },
  },

  methods: {
    registerCrumbs() {
      this.$crumbs.register([
        {
          text: 'Meeting Overview',
          path: {
            name: 'curriculum_assets_meeting',
          },
        },
        {
          text: this.meeting.name || this.meeting.id,
          active: true,
        },
      ]);
    },
    enterEditMode() {
      this.renderMode = false;
    },
    enterRenderMode() {
      this.renderMode = true;
    },
    updateMeetingRequest() {
      const meetingMetaPayload = {
        name: this.meetingMeta.title,
        description: this.meetingMeta.description,
        tags: this.meetingMeta.meetingTags,
        support_function: this.meetingMeta.supportFunction,
        is_progress_review: this.meetingMeta.hasForm,
        is_adhoc: this.meetingMeta.isAdhoc || false,
        requires_learner_signature: this.meetingMeta.requiresLearnerSignature ?? true,
        share_with_employer_default: this.meetingMeta.shareWithEmployerDefault ?? true,
      };
      const logPayload = { meetingId: this.meetingId, meetingMetaPayload };
      this.$logger.info('Updating meeting metadata', logPayload);
      return this.$http.put(`/api/curriculum/admin/meeting/${this.meetingId}`, meetingMetaPayload)
        .then(() => {
          this.$logger.info('Updated meeting metadata', logPayload);
          return Promise.resolve({ success: true });
        })
        .catch(error => {
          this.$logger.autowarn('Error updating meeting metadata', logPayload, error);
          return Promise.resolve({ success: false, error });
        });
    },
    createMeetingFormRequest() {
      this.$logger.info('Creating new meeting form', { meetingId: this.meeting.id });
      return this.$http.post('/api/curriculum/admin/meeting/form', this.meetingFormPayload)
        .then(response => {
          this.$logger.info('Created new meeting form', { meetingId: this.meeting.id });
          this.meetingForm.id = response.data.id;
          return Promise.resolve({ success: true, meetingFormId: this.meetingForm.id });
        })
        .catch(error => {
          this.$logger.autowarn('Error creating new meeting form', { meetingId: this.meeting.id }, error);
          return Promise.resolve({ success: false, error });
        });
    },
    updateMeetingFormRequest() {
      const logPayload = { meetingId: this.meeting.id, meetingFormId: this.meeting.meeting_form_id };
      this.$logger.info('Updating existing meeting form', logPayload);
      return this.$http.put(`/api/curriculum/admin/meeting/form/${this.meeting.meeting_form_id}`, this.meetingFormPayload)
        .then(() => {
          this.$logger.info('Updated meeting form', logPayload);
          return Promise.resolve({ success: true });
        })
        .catch(error => {
          this.$logger.autowarn('Error updating meeting form', logPayload, error);
          return Promise.resolve({ success: false, error });
        });
    },
    deleteMeetingFormRequest() {
      const logPayload = { meetingId: this.meeting.id, meetingFormId: this.meeting.meeting_form_id };
      this.$logger.info('Deleting meeting form', logPayload);
      return this.$http.delete(`/api/curriculum/admin/meeting/form/${this.meeting.meeting_form_id}`)
        .then(() => {
          this.$logger.info('Deleted meeting form', logPayload);
          return Promise.resolve({ success: true });
        })
        .catch(error => {
          this.$logger.autowarn('Error deleting meeting form', logPayload, error);
          return Promise.resolve({ success: false, error });
        });
    },
    selectMeetingFormRequest() {
      if (this.meetingMeta.hasForm) {
        if (this.meetingForm.id) {
          return this.updateMeetingFormRequest;
        }
        return this.createMeetingFormRequest;
      }
      if (this.meetingForm.id) {
        return this.deleteMeetingFormRequest;
      }
      return null;
    },
    getMeeting() {
      this.meetingReady = false;
      this.$logger.info('Getting meeting', { meetingId: this.meetingId }, true);
      this.$http.get(`/api/curriculum/admin/meeting/${this.meetingId}`).then(response => {
        this.meeting = response.data.meeting;
        this.$logger.info('Got meeting', { meetingId: this.meetingId });
        this.registerCrumbs();
      }).catch(err => {
        if (this.$http.errIn(err, [404])) {
          this.$logger.warn('Meeting not found', { meetingId: this.meetingId }, err);
        } else if (this.$http.isWarning(err)) {
          this.$logger.warn('Error getting meeting', { meetingId: this.meetingId }, err);
          this.$ktoast.showError(err, true);
        } else {
          this.$logger.error('Error getting meeting', { meetingId: this.meetingId }, err);
          this.$ktoast.showError(err, true);
        }
      }).then(() => {
        this.meetingReady = true;
      });
    },
    async updateMeeting() {
      this.updateInProgress = true;
      const updateMeetingResult = await this.updateMeetingRequest();
      let success = updateMeetingResult.success;
      let error = updateMeetingResult.error;
      const meetingFormRequest = this.selectMeetingFormRequest();
      if (success && meetingFormRequest) {
        const meetingFormResult = await meetingFormRequest();
        success = meetingFormResult.success;
        error = meetingFormResult.error;
      }
      // All requests that needed to be made have finished
      this.updateInProgress = false;
      if (success) {
        this.$ktoast.success('Meeting updated');
        this.getMeeting();
      } else {
        this.$ktoast.showError(error);
      }
    },
  },
};
</script>
